import { ChangeDetectionStrategy, Component, inject, input, output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { IconComponent, SelectComponent, SelectOption } from 'libs/shared';
import { SectorState, SectorMode } from 'libs/domain';
import { SectorUiService } from 'libs/features/sectors/services/sector-ui.service';
import { FiltersPage } from 'libs/features/sectors/models/sector.interfaces';

const DEFAULT_ORDER_OPTIONS: SelectOption[] = [
  { label: 'Nombre (A-Z)', value: 'NAME:ASC' },
  { label: 'Nombre (Z-A)', value: 'NAME:DESC' },
  { label: 'Estado', value: 'STATE:ASC' },
  { label: 'Modo', value: 'MODE:ASC' },
];

/**
 * Contenido reutilizable del filtro de sectores/tomas (Estado, Modo, Ordenar por).
 * Es presentacional puro: NO conoce modales ni cómo se muestra. Cada pantalla lo
 * envuelve en su carcasa (panel responsive en web, modal Ionic en app) y decide la
 * presentación. Emite `filtersChange` en vivo y `dirtyChange` para que la carcasa
 * sepa cuándo ofrecer "Aceptar".
 */
@Component({
  selector: 'lib-sectors-filters',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SelectComponent, IconComponent],
  templateUrl: './sectors-filters.component.html',
  // El host es display:inline por defecto; sin esto se rompe el cálculo de altura
  // del contenedor con scroll que lo envuelve (panel web en pantalla estrecha).
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectorsFilters implements OnInit {
  public sectorUiService = inject(SectorUiService);
  private fb = inject(FormBuilder);

  filters = input<FiltersPage>({});
  /** Opciones del desplegable "Ordenar por". Punto de extensión por pantalla. */
  orderOptions = input<SelectOption[]>(DEFAULT_ORDER_OPTIONS);
  /** Muestra el resumen de filtros activos (chips + "Limpiar filtros"). */
  showActiveFilters = input<boolean>(true);

  filtersChange = output<FiltersPage>();
  dirtyChange = output<boolean>();

  form!: FormGroup;

  states = Object.values(SectorState);
  stateGroups: { label: string; values: string[] }[] = [];
  modes = Object.values(SectorMode);

  ngOnInit() {
    this.generateStateGroups();

    const f = this.filters();
    const initialStates = f.states ? [...f.states] : [];
    const initialModes = f.modes ? [...f.modes] : [];
    const initialOrder = f.sort_by
      ? `${f.sort_by.toUpperCase()}:${f.sort_direction || 'ASC'}`
      : 'NAME:ASC';

    this.form = this.fb.group({
      states: [initialStates],
      modes: [initialModes],
      order: [initialOrder],
    });

    // Emite el valor actual en cada cambio (filtrado en vivo en el panel desktop)
    // y el estado dirty para que la carcasa muestre el botón de confirmar.
    this.form.valueChanges.subscribe(() => {
      this.filtersChange.emit(this.buildResult());
      this.dirtyChange.emit(this.form.dirty);
    });
  }

  get statesValue(): string[] { return this.form?.get('states')?.value || []; }
  get modesValue(): string[] { return this.form?.get('modes')?.value || []; }
  get orderValue(): string { return this.form?.get('order')?.value || 'NAME:ASC'; }

  generateStateGroups() {
    const groups = new Map<string, string[]>();
    this.states.forEach(state => {
      const label = this.sectorUiService.getSectorStateString(state);
      if (!groups.has(label)) {
        groups.set(label, []);
      }
      groups.get(label)!.push(state);
    });
    this.stateGroups = Array.from(groups.entries()).map(([label, values]) => ({ label, values }));
  }

  getStateGroupClass(label: string, groupValues: string[]): string {
    const isSelected = this.isStateGroupSelected(groupValues);

    if (!isSelected) {
      return 'bg-white text-gray-400 border border-gray-300 hover:bg-gray-50';
    }

    switch (label) {
      case 'Riego':
        return 'bg-blue-light-100 text-blue-light-600 border-blue-light-100';
      case 'Inicia riego':
        return 'bg-success-100 text-success-600 border-success-100';
      case 'No comunica':
        return 'bg-yellow-100 text-yellow-500 border-yellow-100';
      case 'Incidencia':
        return 'bg-warning-100 text-warning-600 border-warning-100';
      case 'Avería':
        return 'bg-error-50 text-error-600 border-error-50';
      case 'Fuga':
        return 'bg-warning-50 text-warning-600 border-warning-50';
      case 'No riego':
        return 'bg-gray-100 text-gray-600 border-gray-100';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-100';
    }
  }

  getModeClass(mode: string): string {
    const isSelected = this.modesValue.includes(mode);

    if (!isSelected) {
      return 'bg-white text-gray-400 border border-gray-300 hover:bg-gray-50';
    }

    switch (mode) {
      case 'AUTOMATIC':
        return 'bg-brand-50 text-brand-600 border-brand-50';
      case 'MANUAL':
        return 'bg-manual-25 text-manual-500 border-manual-25';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-100';
    }
  }

  isStateGroupSelected(groupValues: string[]): boolean {
    return groupValues.length > 0 && groupValues.every(s => this.statesValue.includes(s));
  }

  getBadgeClass(label: string): string {
    return this.sectorUiService.getStateBadgeClass(label);
  }

  getModeBadgeClass(mode: string): string {
    return this.sectorUiService.getModeBadgeClass(mode);
  }

  toggleStateGroup(groupValues: string[]) {
    const current = this.statesValue;
    const allSelected = groupValues.every(s => current.includes(s));

    let newValue: string[];
    if (allSelected) {
      newValue = current.filter(s => !groupValues.includes(s));
    } else {
      const missing = groupValues.filter(s => !current.includes(s));
      newValue = [...current, ...missing];
    }

    // markAsDirty antes de patchValue para que valueChanges vea el dirty correcto.
    this.form.markAsDirty();
    this.form.patchValue({ states: newValue });
  }

  toggleMode(mode: string) {
    const current = this.modesValue;
    let newValue: string[];

    if (current.includes(mode)) {
      newValue = current.filter(m => m !== mode);
    } else {
      newValue = [...current, mode];
    }

    this.form.markAsDirty();
    this.form.patchValue({ modes: newValue });
  }

  clearFilters() {
    this.form.markAsDirty();
    this.form.patchValue({ states: [], modes: [] });
  }

  setOrder(value: string) {
    this.form.markAsDirty();
    this.form.get('order')?.setValue(value);
  }

  private buildResult(): FiltersPage {
    const { states, modes, order } = this.form.value;
    const [sortBy, sortDirection] = order.split(':');

    return {
      ...this.filters(),
      states,
      modes,
      sort_by: sortBy,
      sort_direction: sortDirection as 'ASC' | 'DESC',
    };
  }
}
